import React, { useMemo } from 'react';
import { Tag } from '../common/Tag';
import { STD_TEMPLATE } from '../../utils/constants';

export const calculateScores = (template, responses) => {
  const sections = template.sections.map((section) => {
    let weightedSum = 0;
    let maxWeightedSum = 0;
    let missing = 0;

    section.questions.forEach((q) => {
      const r = responses[q.id];
      let score = null;
      if (r == null || r === '') {
        missing++;
        maxWeightedSum += q.weight * 10;
      } else {
        if (q.type === 'yesno') score = r === 'yes' ? 10 : r === 'partial' ? 5 : 0;
        if (q.type === 'scale') score = Number(r) || 0;
        if (score != null) {
          weightedSum += score * q.weight;
          maxWeightedSum += q.weight * 10;
        }
      }
    });

    const pct = maxWeightedSum > 0 ? Math.round((weightedSum / maxWeightedSum) * 100) : 0;
    return { id: section.id, name: section.name, weight: section.weight, score: pct, missing };
  });

  let totalWeighted = 0;
  let totalWeight = 0;
  sections.forEach((s) => {
    totalWeighted += s.score * s.weight;
    totalWeight += s.weight;
  });

  const overall = totalWeight > 0 ? Math.round(totalWeighted / totalWeight) : 0;

  let band, bandLabel, bandVariant;
  if (overall >= 85) {
    band = 'strong';
    bandLabel = 'Strong fit / Low risk';
    bandVariant = 'ok';
  } else if (overall >= 70) {
    band = 'acceptable';
    bandLabel = 'Acceptable / Medium risk';
    bandVariant = 'info';
  } else if (overall >= 50) {
    band = 'conditional';
    bandLabel = 'Conditional / High risk';
    bandVariant = 'warn';
  } else {
    band = 'reject';
    bandLabel = 'Not recommended / Critical risk';
    bandVariant = 'danger';
  }

  return { sections, overall, band, bandLabel, bandVariant };
};

function ComparisonView({ feasibility }) {
  const scoresByCRO = useMemo(() => {
    const m = {};
    (feasibility.cros || []).forEach((cro) => {
      m[cro] = calculateScores(STD_TEMPLATE, feasibility.responses?.[cro] || {});
    });
    return m;
  }, [feasibility]);

  const sorted = useMemo(
    () => Object.entries(scoresByCRO).sort((a, b) => b[1].overall - a[1].overall),
    [scoresByCRO]
  );

  return (
    <div>
      <div className="grid mb-4" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(sorted.length, 3)}, 1fr)`, gap: 14 }}>
        {sorted.map(([cro, s], i) => (
          <div key={cro} className="card card-pad" style={i === 0 && s.overall > 0 ? { borderColor: 'var(--ink)', borderWidth: 2 } : null}>
            <div className="flex between center mb-2">
              <div className="medium sm">{cro}</div>
              {i === 0 && s.overall > 0 && <Tag variant="solid">Recommended</Tag>}
            </div>
            <div className="metric-value" style={{ fontSize: 36, color: s.overall >= 85 ? 'var(--ok)' : s.overall >= 70 ? 'var(--ink)' : s.overall >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
              {s.overall}
              <span style={{ fontSize: 14, color: 'var(--ink-3)', fontFamily: 'var(--sans)', fontWeight: 400 }}> / 100</span>
            </div>
            <Tag variant={s.bandVariant} dot>{s.bandLabel}</Tag>
            <div className="xs muted mt-2">
              {s.sections.reduce((a, sec) => a + sec.missing, 0)} unanswered · {s.sections.filter((sec) => sec.score < 60).length} weak sections
            </div>
          </div>
        ))}
      </div>

      <div className="section-label">Section breakdown</div>
      <div className="card">
        <table className="tbl">
          <thead>
            <tr>
              <th>Section</th>
              <th className="num" style={{ textAlign: 'right' }}>Weight</th>
              {sorted.map(([cro]) => (
                <th key={cro} className="num" style={{ textAlign: 'right' }}>{cro.split(' ')[0]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {STD_TEMPLATE.sections.map((section) => (
              <tr key={section.id}>
                <td>{section.name}</td>
                <td className="num" style={{ textAlign: 'right' }}>{section.weight}</td>
                {sorted.map(([cro, s]) => {
                  const sec = s.sections.find((x) => x.id === section.id);
                  const color = sec.score >= 85 ? 'var(--ok)' : sec.score >= 70 ? 'var(--ink)' : sec.score >= 50 ? 'var(--warn)' : 'var(--danger)';
                  return (
                    <td key={cro} className="num" style={{ textAlign: 'right', color, fontWeight: 500 }}>
                      {sec.score}
                      {sec.missing > 0 && <sup className="muted" style={{ fontSize: 9 }}> {sec.missing}*</sup>}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ background: 'var(--surface-3)' }}>
              <td className="bold">Overall weighted</td>
              <td></td>
              {sorted.map(([cro, s]) => (
                <td key={cro} className="num bold" style={{ textAlign: 'right' }}>{s.overall}</td>
              ))}
            </tr>
          </tbody>
        </table>
        <div className="xs muted" style={{ padding: '0 14px 12px' }}>* = unanswered question(s)</div>
      </div>
    </div>
  );
}

export default ComparisonView;